/**
 * Journal des appels d'API — la source de données de l'« Inspecteur d'API ».
 *
 * C'est un magasin (store) minimal, sans aucune bibliothèque de gestion
 * d'état : une liste en mémoire, une liste d'abonnés, et une fonction de
 * notification. Il est consommé par React via `useSyncExternalStore`, le hook
 * prévu exactement pour ça (voir `hooks/use-api-log.ts`).
 *
 * L'alimentation se fait dans `lib/dashlite.ts`, qui passe au SDK une
 * implémentation de `fetch` instrumentée. Le SDK n'a donc **aucune**
 * connaissance de ce journal : votre propre intégration peut brancher ici de
 * la télémétrie, un traceur, ou rien du tout.
 */

import { randomId } from './utils';

/** État d'un appel dans son cycle de vie. */
export type ApiCallState = 'pending' | 'success' | 'error' | 'network-error';

export type ApiCall = {
    id: string;
    /** Horodatage local du départ de la requête. */
    startedAt: number;
    method: string;
    /** Chemin relatif à `/api/v1`, avec sa query string — lisible en liste. */
    endpoint: string;
    /** URL absolue réellement appelée. */
    url: string;
    /** En-têtes envoyés, `Authorization` tronqué (voir `maskHeaders`). */
    requestHeaders: Record<string, string>;
    /** Corps JSON envoyé, déjà désérialisé. `undefined` sur un GET. */
    requestBody: unknown;
    state: ApiCallState;
    status: number | null;
    durationMs: number | null;
    /** Enveloppe complète renvoyée par l'API (`{ data }` ou `{ error }`). */
    responseBody: unknown;
    /** Le `error.code` du catalogue, quand la réponse est une erreur. */
    errorCode: string | null;
};

/** Au-delà, les appels les plus anciens sont oubliés. */
const MAX_ENTRIES = 60;

let entries: ApiCall[] = [];
let listeners: Array<() => void> = [];

function emit(): void {
    // Nouvelle référence de tableau à chaque changement : c'est ce qui permet
    // à `useSyncExternalStore` de détecter la mise à jour.
    entries = entries.slice(0, MAX_ENTRIES);
    listeners.forEach((listener) => listener());
}

/**
 * Masque les en-têtes sensibles avant journalisation.
 *
 * La clé de site est publique et son affichage est justement pédagogique : on
 * la garde. Le token Bearer, lui, est un secret de session — on n'en montre
 * que la forme.
 */
function maskHeaders(headers: Record<string, string>): Record<string, string> {
    const masked: Record<string, string> = { ...headers };

    if (masked.Authorization) {
        const token = masked.Authorization.replace(/^Bearer\s+/i, '');
        masked.Authorization = `Bearer ${token.slice(0, 4)}…${token.slice(-4)}`;
    }

    return masked;
}

export const apiLog = {
    /** Abonnement utilisé par `useSyncExternalStore`. */
    subscribe(listener: () => void): () => void {
        listeners = [...listeners, listener];

        return () => {
            listeners = listeners.filter((registered) => registered !== listener);
        };
    },

    /** Instantané courant — référence stable tant que rien n'a changé. */
    getSnapshot(): ApiCall[] {
        return entries;
    },

    /** Enregistre le départ d'une requête et retourne son identifiant. */
    start(input: {
        method: string;
        url: string;
        endpoint: string;
        headers: Record<string, string>;
        body: unknown;
    }): string {
        const id = randomId();

        entries = [
            {
                id,
                startedAt: Date.now(),
                method: input.method.toUpperCase(),
                endpoint: input.endpoint,
                url: input.url,
                requestHeaders: maskHeaders(input.headers),
                requestBody: input.body,
                state: 'pending',
                status: null,
                durationMs: null,
                responseBody: null,
                errorCode: null,
            },
            ...entries,
        ];

        emit();

        return id;
    },

    /** Complète une entrée à la réception de la réponse. */
    finish(
        id: string,
        result: {
            status: number;
            durationMs: number;
            body: unknown;
            errorCode: string | null;
        },
    ): void {
        entries = entries.map((entry) =>
            entry.id === id
                ? {
                      ...entry,
                      state: result.status >= 200 && result.status < 300 ? 'success' : 'error',
                      status: result.status,
                      durationMs: result.durationMs,
                      responseBody: result.body,
                      errorCode: result.errorCode,
                  }
                : entry,
        );

        emit();
    },

    /** La requête n'a jamais abouti : hors ligne, CORS, DNS… */
    fail(id: string, durationMs: number, reason: string): void {
        entries = entries.map((entry) =>
            entry.id === id
                ? {
                      ...entry,
                      state: 'network-error',
                      durationMs,
                      responseBody: { message: reason },
                      errorCode: null,
                  }
                : entry,
        );

        emit();
    },

    clear(): void {
        entries = [];
        emit();
    },
};
